import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { NotificationVariant } from 'src/js/notification/notification';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';
import { isNilOrEmpty } from 'src/js/util/global-util';

import {
  EssenceResponse,
  EssenceViewModel,
  GetAllEssencesResponse,
  GetAllResolutionsResponse,
  ResolutionViewModel,
  mapEssenceViewModel,
  mapEssencesResponse,
  mapResolutionResponse,
} from './essence-models';

const essenceModels: EssenceViewModel[] = [];
createWatchStore(essenceModels);
export default essenceModels;

export const resolutions: ResolutionViewModel[] = [];
createWatchStore(resolutions);

export const saveResponseToEssences = (response: EssenceViewModel[]) => {
  updateWatchStore(essenceModels, response);
};

const replaceViewModel = (response: EssenceResponse, t: TFunction, selected = false) => {
  const viewModel = mapEssenceViewModel(response, t);
  const essences = [...essenceModels];
  const index = essences.findIndex((e) => e.id === viewModel.id);
  if (index > -1) {
    essences.splice(index, 1, { ...viewModel, selected: selected });
  }

  saveResponseToEssences(essences);
};

const deleteViewModel = (model: EssenceViewModel) => {
  const essences = [...essenceModels];
  const index = essences.findIndex((e) => e.id === model.id);
  if (index > -1) {
    essences.splice(index, 1);
  }

  saveResponseToEssences(essences);
};

export const fetchEssences = (t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllEssencesResponse>(APIPath.essences.list).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        const viewModels = mapEssencesResponse(response, t);
        saveResponseToEssences(viewModels);
      }
    }
    return res;
  });
};

export const fetchEssenceResolutions = () => {
  return apiClientWithoutHandler.genericController
    .get<GetAllResolutionsResponse>(APIPath.essences.resolutions)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const viewModels = mapResolutionResponse(response);
          updateWatchStore(resolutions, viewModels);
        }
      }
      return res;
    });
};

export const startStopEssence = (model: EssenceViewModel, start: boolean, selected: boolean, t: TFunction) => {
  const path = APIPathWithArguments(start ? APIPath.essences.start : APIPath.essences.stop, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<EssenceResponse>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t, selected);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, null, null, t);
      return res;
    });
};

export const fetchEssenceStatistics = (
  model: EssenceViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<EssenceViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.essences.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.get<EssenceResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapEssenceViewModel(response, t));
      }
    }
    return res;
  });
};

export const resetEssenceStatistics = (
  model: EssenceViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<EssenceViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.essences.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.delete<EssenceResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapEssenceViewModel(response, t));
      }
    }
    return res;
  });
};

export const createEssence = (model: EssenceViewModel, t: TFunction) => {
  return apiClientWithoutHandler.genericController
    .post<EssenceResponse>(APIPath.essences.add, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const viewModel = mapEssenceViewModel(response, t);
          const essences = [...essenceModels];
          essences.splice(0, 0, { ...viewModel, selected: false });
          saveResponseToEssences(essences);
        }
      }
      return res;
    })
    .then((res) => {
      const response = res.data;
      if (!isNilOrEmpty(response.warning)) {
        createAndDispatchNotification(response.warning, NotificationVariant.WARNING, t);
      } else {
        notificationHandler(
          res,
          t('essence.notifications.saveSuccessMsg'),
          response.message || t('essence.notifications.saveErrorMsg'),
          t,
        );
      }
      return res;
    });
};

export const updateEssence = (model: EssenceViewModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.essences.update, { id: model.id });
  return apiClientWithoutHandler.genericController
    .put<EssenceResponse>(path, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        t('essence.notifications.changeSuccessMsg'),
        t('essence.notifications.changeErrorMsg'),
        t,
      );
      return res;
    });
};

export const deleteEssence = (model: EssenceViewModel) => {
  const path = APIPathWithArguments(APIPath.essences.delete, { id: model.id });
  return apiClientWithoutHandler.genericController.delete(path).then((res) => {
    if (res.ok) {
      deleteViewModel(model);
    }
    return res;
  });
};
